import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { Route, Routes, useParams } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";

import { ProviderContext } from "../Context";
import { GET_PROVIDER_WITH_VERSION } from "../gql";
import { TerraformProviderWithVersions } from "../types";
import useProviderVersionResolver from "../useProviderVersionResolver";
import Assets from "./Assets";
import Changelog from "./Changelog";
import { ProviderVersionDocsContext } from "./Context";
import Layout from "./Layout";
import Overview from "./Overview";
import PageWrapper from "./PageWrapper";
import { transformApiDocsToTree } from "./helpers";

const ProviderVersionDocs = () => {
  const { provider: providerFromContext } = useTypedContext(ProviderContext);
  const { providerId, versionNumber: versionParam } = useParams<{
    providerId: string;
    versionNumber: string;
  }>();

  // Resolve "latest" to actual version number using provider from context
  const { versionNumber } = useProviderVersionResolver(versionParam, providerFromContext);

  const { error, loading, data } = useQuery(GET_PROVIDER_WITH_VERSION, {
    variables: { id: providerId!, versionNumber },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
    skip: !providerId || !versionNumber,
  });

  const provider = data?.terraformProvider;
  const versionData = provider?.version;

  // Build provider object for context
  const providerWithVersions: TerraformProviderWithVersions | undefined = useMemo(() => {
    if (!provider || !versionData) return undefined;

    return {
      id: provider.id,
      description: provider.description,
      space: provider.space,
      latestVersionNumber: provider.latestVersionNumber,
      labels: provider.labels,
      public: provider.public,
      allowedActions: provider.allowedActions,
      spaceDetails: provider.spaceDetails,
      versions: provider.versions,
    };
  }, [provider, versionData]);

  // Transform API docs to tree structure
  const docs = useMemo(() => {
    if (!versionData?.docs?.files) return [];

    return transformApiDocsToTree(
      versionData.docs.files,
      versionData.docs.overviewContent ?? undefined
    );
  }, [versionData]);

  // Memoize context value to prevent unnecessary re-renders
  // Must be called before any conditional returns (Rules of Hooks)
  const contextValue = useMemo(
    () => ({
      provider: providerWithVersions!,
      version: versionData!,
      docs,
    }),
    [providerWithVersions, versionData, docs]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !provider) {
    return <PageLoading />;
  }

  if (!providerWithVersions || !versionData) {
    return <NotFoundPage />;
  }

  return (
    <ProviderVersionDocsContext.Provider value={contextValue}>
      <PageWrapper>
        <Routes>
          <Route element={<Layout />}>
            <Route path="assets" element={<Assets />} />
            <Route path="changelog" element={<Changelog />} />
            <Route path="*" element={<Overview />} />
          </Route>
        </Routes>
      </PageWrapper>
    </ProviderVersionDocsContext.Provider>
  );
};

export default ProviderVersionDocs;
